import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ChangeDetectionStrategy,
    ElementRef,
    computed,
    effect,
    inject,
    input,
    viewChild,
} from '@angular/core';
import { ClusterService, OidcService, TokenBrokerService } from '@fry-it/risr-core';
import {
    defineRisrAiUsage,
    type RisrAiUsageConfig,
    type RisrAiUsageElement,
    type UserNameResolver,
} from '@fry-it/risr-ai';

/** RFC 8707 resource indicator for the `risrai` audience. */
const RISRAI_RESOURCE = 'https://risr.global/resources/risrai';

/**
 * Smart container that mounts the framework-agnostic `<risr-ai-usage>`
 * Web Component (from `@fry-it/risr-ai`) into a Kaizen/Assembly app shell.
 *
 * Wiring mirrors `RisrAiAssistantHostComponent`: the widget only renders when
 * the current organisation has a `risrai` backend URL configured (populated
 * from `conf/zorg.conf` at cluster setup time). Auth token and organisation
 * are sourced from `OidcService`/`TokenBrokerService`; the exchanged token
 * must carry the `risrai:usage:stats` scope for the backend to authorise the
 * org-wide stats read.
 *
 * Subject → display-name resolution stays host-owned: pass an optional
 * `resolveUserNames` callback (batched, sync or async). When omitted the
 * component falls back to showing the raw opaque subject.
 */
@Component({
    selector: 'risr-ai-usage-host',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    template: `
        @if (apiBaseUrl() && organisation()) {
            <risr-ai-usage #el></risr-ai-usage>
        }
    `,
})
export class RisrAiUsageHostComponent {
    private readonly cluster = inject(ClusterService);
    private readonly oidc = inject(OidcService);
    private readonly tokenBroker = inject(TokenBrokerService);

    /** Optional host-side subject → display-name resolver (batched). */
    readonly resolveUserNames = input<UserNameResolver | undefined>(undefined);

    /** Max entries in the top-users leaderboard (component default is 10). */
    readonly topUsersLimit = input<number | undefined>(undefined);

    private readonly el = viewChild<ElementRef<RisrAiUsageElement>>('el');

    protected readonly apiBaseUrl = computed(() => {
        try {
            return this.cluster.getCurrentUrls().risrai ?? '';
        } catch {
            return '';
        }
    });

    protected readonly organisation = computed(
        () => this.oidc.$currentOrganisation() ?? '',
    );

    private readonly getToken: RisrAiUsageConfig['getToken'] = async () => {
        const organisation = this.organisation();
        if (!organisation) {
            return '';
        }
        // Same org-binding encoding as the assistant host: the active
        // organisation is passed as a dynamic `org:<id>` scope so the IdP can
        // validate membership and stamp a trusted `organisation` claim on the
        // exchanged `risrai` token. Stats requires the `risrai:usage:stats`
        // scope; the backend enforces authorisation regardless of UI gating.
        const scope = [`org:${organisation}`, 'risrai:usage:stats'].join(' ');
        return this.tokenBroker.getToken(RISRAI_RESOURCE, scope);
    };

    constructor() {
        defineRisrAiUsage();

        effect(() => {
            const host = this.el()?.nativeElement;
            const apiBaseUrl = this.apiBaseUrl();
            const organisation = this.organisation();
            if (!host || !apiBaseUrl || !organisation) {
                return;
            }
            const config: RisrAiUsageConfig = {
                apiBaseUrl,
                organisation,
                getToken: this.getToken,
            };
            const resolveUserNames = this.resolveUserNames();
            if (resolveUserNames) {
                config.resolveUserNames = resolveUserNames;
            }
            const topUsersLimit = this.topUsersLimit();
            if (topUsersLimit !== undefined) {
                config.topUsersLimit = topUsersLimit;
            }
            host.config = config;
        });
    }
}
